<template>
  <main
    class="campaign-area"
    :dir="locale === 'en' ? 'ltr' : 'rtl'"
  >
    <header><h1>{{ words.title }}</h1><p>{{ words.hint }}</p><small>{{ reportWords.reportTimezone }}</small></header>
    <form
      class="campaign-panel campaign-grid"
      @submit.prevent="load"
    >
      <CampaignReportDate
        :value="from"
        :locale="locale"
        :label="words.from"
        field="operations-from"
        @change="from = $event"
      />
      <CampaignReportDate
        :value="to"
        :locale="locale"
        :label="words.to"
        field="operations-to"
        @change="to = $event"
      />
      <button
        class="primary"
        :disabled="loading || !$can('read', 'operationalReporting')"
      >
        {{ words.filter }}
      </button>
    </form>
    <p
      v-if="loading"
      role="status"
    >
      {{ words.loading }}
    </p>
    <p
      v-if="message"
      class="campaign-message"
      role="alert"
    >
      {{ message }}
    </p>
    <section
      v-if="report"
      class="campaign-panel"
      aria-live="polite"
    >
      <h2>{{ words.coverage }}</h2><p>{{ availability }}</p>
      <p>{{ words.conversion }}</p><p>{{ words.responsesHint }}</p>
      <p>{{ words.refreshed }}: <bdi dir="ltr">{{ report.freshness.lastObservedAt || words.unknown }}</bdi> · UTC</p>
    </section>
    <template v-if="available">
      <section
        v-for="item in stages"
        :key="item.stage"
        class="campaign-panel campaign-table-wrap"
      >
        <h2>{{ words[item.stage] }}</h2>
        <table><caption>{{ words.attemptsHint }}</caption><thead><tr><th scope="col">
          {{ words.metric }}
        </th><th scope="col">
          {{ words.value }}
        </th></tr></thead>
          <tbody><tr><th scope="row">
                   {{ words.attempts }}
                 </th><td>{{ count(item.attempts) }}</td></tr>
            <tr
              v-for="outcome in outcomes"
              :key="outcome"
            >
              <th scope="row">
                {{ words[outcome] }}
              </th><td>{{ count(item.outcomes[outcome]) }}</td>
            </tr>
            <tr><th scope="row">
              {{ words.averageLatency }}
            </th><td>{{ count(item.latencyMs.average) }}</td></tr>
            <tr><th scope="row">
              {{ words.maxLatency }}
            </th><td>{{ count(item.latencyMs.max) }}</td></tr></tbody>
        </table>
      </section>
      <p
        v-if="!report.daily.length"
        role="status"
      >
        {{ words.empty }}
      </p>
      <section
        v-else
        class="campaign-panel campaign-table-wrap"
      >
        <table><caption>{{ words.daily }}</caption><thead><tr><th scope="col">
          {{ words.day }}
        </th><th scope="col">
          {{ words.stage }}
        </th><th scope="col">
          {{ words.outcome }}
        </th><th scope="col">
          {{ words.attempts }}
        </th></tr></thead>
          <tbody><tr
            v-for="(row, index) in report.daily"
            :key="index"
          >
            <td><bdi dir="ltr">{{ row.day }}</bdi></td><td>{{ words[row.stage] || words.unknown }}</td><td>{{ words[row.outcome] || words.unknown }}</td><td>{{ count(row.attempts) }}</td>
          </tr></tbody>
        </table>
      </section>
    </template>
  </main>
</template>
<script>
import axios from '@/libs/axios'
import messages from './operational-report-messages'
import reportMessages from './messages'
import './campaign.css'
import CampaignReportDate from './CampaignReportDate.vue'

export default {
  components: { CampaignReportDate },
  data() {
    const now = new Date()
    return {
      from: new Date(now.getTime() - 7 * 86400000).toISOString().slice(0, 10), to: now.toISOString().slice(0, 10), report: null, loading: false, message: '', sequence: 0, outcomes: ['response', 'timeout', 'unavailable', 'rejected', 'unknown'],
    }
  },
  computed: {
    locale() { return ['fa', 'en', 'ar'].includes(this.$i18n.locale) ? this.$i18n.locale : 'fa' },
    words() { return messages[this.locale] },
    reportWords() { return reportMessages[this.locale] },
    available() { return !!this.report && this.report.freshness.enabled === true && this.report.freshness.ready === true },
    availability() { if (!this.report || !this.report.freshness.enabled) return this.words.disabled; return this.report.freshness.ready ? this.words.partial : this.words.notReady },
    stages() { return (this.report && this.report.stages) || [] },
  },
  beforeDestroy() { this.sequence += 1 },
  methods: {
    count(value) { return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? String(value) : this.words.unknown },
    params() {
      const validDay = value => /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value
      if (!validDay(this.from) || !validDay(this.to)) throw new Error('INVALID_RANGE')
      const from = new Date(`${this.from}T00:00:00Z`); const to = new Date(`${this.to}T00:00:00Z`)
      to.setUTCDate(to.getUTCDate() + 1)
      if (to <= from || to - from > 31 * 86400000) throw new Error('INVALID_RANGE')
      return { from: from.toISOString(), to: to.toISOString() }
    },
    validReport(report) {
      if (!report || report.timezone !== 'UTC' || report.coverage !== 'observed-attempts-only' || report.journeyConversion !== 'unavailable' || !report.freshness || report.freshness.completeness !== 'unknown' || typeof report.freshness.enabled !== 'boolean' || typeof report.freshness.ready !== 'boolean' || !Array.isArray(report.stages) || !Array.isArray(report.daily)) return false
      return report.stages.every(item => ['reservation', 'finalization'].includes(item.stage) && item.outcomes && item.latencyMs) && report.daily.length <= 310
    },
    async load() {
      if (!this.$can('read', 'operationalReporting')) { this.report = null; this.message = this.words.forbidden; return }
      this.sequence += 1; const { sequence } = this; this.loading = true; this.message = ''; this.report = null
      try {
        const params = this.params(); const response = await axios.get('operational-reports', { params, timeout: 10000 })
        if (sequence !== this.sequence) return
        if (!this.validReport(response.data)) throw new Error('INVALID_REPORT')
        this.report = response.data
      } catch (error) {
        if (sequence === this.sequence) {
          const status = error && error.response && error.response.status
          const code = error && error.response && error.response.data && error.response.data.message
          this.message = this.words.failed
          if (error.message === 'INVALID_RANGE' || code === 'OPERATIONAL_REPORT_RANGE') this.message = this.words.invalidRange
          else if (status === 401) this.message = this.words.session
          else if (status === 403) this.message = this.words.forbidden
          else if (code === 'OPERATIONAL_REPORT_NOT_READY') this.message = this.words.notReady
        }
      } finally { if (sequence === this.sequence) this.loading = false }
    },
  },
}
</script>
